import { yupResolver } from "@hookform/resolvers/yup";
import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Autocomplete, Box, Button, Chip, Divider, Icon, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { RHFCheckbox, RHFSelect, RHFTextField } from "src/components/hook-form";
import FormProvider from "src/components/hook-form/form-provider";
import { useCurrentUserQuery } from "src/context/api/auth";
import { useUploadMutation } from "src/context/api/medias";
import { useGetAdAccountsQuery } from "src/context/api/resources";
import { ErrorResponse } from "src/context/api/shared/models";
import { useCreateWorkspaceMutation, useGetClientsNameQuery, useGetWorkspacesQuery, useUpdateWorkspaceMutation } from "src/context/api/workspaces";
import { useLocales } from "src/locales";
import * as Yup from "yup";
import { ComponentBlock } from "../mui/component-block";
import useIntegrators from "src/hooks/use-integrators";
import { GOOGLE_ANALYTICS_SCOPES } from "src/context/api/google/api";
import Iconify from "src/components/iconify";
import { useRouter } from "src/routes/hooks";
import { useGetCurrenciesQuery } from "src/context/api/currencies/api";

interface Data {
    id?: number;
    name: string;
    url: string;
    bqTable: string;
    custom: boolean;
    averageCart?: number;
    template: string;
    logo?: File;
    currency: string;
    friendlyDomains?: (string | undefined)[];
    isDataReady: boolean;
    showSavings: boolean;
    showPlaAlert: boolean;
    showTxtAlert: boolean;
    shoppingIncluded?: boolean;
    integrations?: {
        email: string;
        scopes: GOOGLE_ANALYTICS_SCOPES[];
    }[];
}

interface Props {
    onCancel?: () => void;
    onSuccess?: () => void;
    values?: Data;
}

export const GOOGLE_ANALYTICS_SCOPES_OPTIONS: GoogleAnalyticsScopesOption[] = [
    { label: "Google Ads", value: GOOGLE_ANALYTICS_SCOPES.gads },
    { label: "Google Search Console", value: GOOGLE_ANALYTICS_SCOPES.gsc },
    { label: "Google Analytics 4", value: GOOGLE_ANALYTICS_SCOPES.ga4 },
];

interface GoogleAnalyticsScopesOption {
    label: string;
    value: GOOGLE_ANALYTICS_SCOPES;
}

export default function WorkspaceCreate({ onCancel, onSuccess, values }: Props) {
    const { t } = useLocales();
    const targets = useIntegrators();
    const schema = Yup.object({
        id: Yup.number().optional(),
        name: Yup.string().required(t("workspaces.form.create.name_required")),
        template: Yup.string().required(t("workspaces.form.create.template_required")),
        url: Yup.string()
            .required(t("workspaces.form.create.url_required"))
            .matches(/^(?!www\.)[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/, t("workspaces.form.create.url_invalid")),
        bqTable: Yup.string().required(t("workspaces.form.create.bqTable_required")),

        averageCart: Yup.number()
            .typeError(t("workspaces.form.create.averageCart_invalid"))
            .test('is-valid-format', t("workspaces.form.create.averageCart_invalid"), (value) => {
                if (value === undefined || value === null) return false;
                const stringValue = value.toString();
                return /^(\d+(\.\d+)?)?$/.test(stringValue);
            })
            .when('template', (template, schema) => {
                return (Array.isArray(template) ? template.includes('E-commerce') : template === 'E-commerce')
                    ? schema.required(t("workspaces.form.create.averageCart_required"))
                    : schema;
            }),
        custom: Yup.boolean().required(t("workspaces.form.create.custom_required")),
        logo: Yup.mixed<File>().optional().test('isFile', t("workspaces.form.logo_must_be_valid_file"), (value) => {
            return value !== undefined ? value instanceof File : true;
        }).test('tooLarge', t('workspaces.form.logo_must_be_under_2mb'), (value) => {
            return value !== undefined ? (value as File).size <= 2 * Math.pow(1000, 2) : true;
        }),
        shoppingIncluded: Yup.boolean().default(false).required("shoppingIncluded is required"),
        currency: Yup.string().required(t("workspaces.form.create.currency_required")),
        friendlyDomains: Yup.array()
            .nullable()
            .of(
                Yup.string().matches(
                    /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/,
                    t("workspaces.form.create.friendly_domains_invalid")
                )
            )
            .notRequired(),
        showPlaAlert: Yup.boolean().default(true).required(t("workspaces.form.create.showPlaAlert_required")),
        showTxtAlert: Yup.boolean().default(true).required(t("workspaces.form.create.showTxtAlert_required")),
        isDataReady: Yup.boolean().default(false).required('isDataReady is required'),
        showSavings: Yup.boolean().default(true).required(t("workspaces.form.create.showSavings_required")),
        integrations: Yup.array().of(Yup.object().shape({
            email: Yup.string().email().required(),
            scopes: Yup.array().of(Yup.string().oneOf(Object.values(GOOGLE_ANALYTICS_SCOPES))).required(),
        })).notRequired(),
    });

    const [newTargetEmail, setNewTargetEmail] = useState<string | null>(null);
    const [newTargetScopes, setNewTargetScopes] = useState<GOOGLE_ANALYTICS_SCOPES[]>([]);

    const defaultValues: Data = useMemo(() => {
        if (values) {
            const { id, name, template, url, bqTable, custom, averageCart, currency, friendlyDomains, showPlaAlert, showTxtAlert, isDataReady, showSavings } = values; return {
                id,
                logo: values.logo,
                name,
                url,
                bqTable,
                averageCart,
                template,
                custom,
                currency,
                friendlyDomains: friendlyDomains || [],
                showPlaAlert: showPlaAlert ?? true,
                showTxtAlert: showTxtAlert ?? true,
                isDataReady: isDataReady ?? false,
                showSavings: showSavings ?? true,
                shoppingIncluded: values?.shoppingIncluded ?? false
            };
        }
        return {
            name: "",
            url: "",
            bqTable: "",
            averageCart: 0,
            template: "",
            custom: false,
            currency: "",
            friendlyDomains: [],
            showPlaAlert: false,
            showTxtAlert: false,
            isDataReady: false,
            showSavings: true,
            shoppingIncluded: false
        };
    }, [values]);

    const methods = useForm<Data>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema) as any, // Force la compatibilité avec les types générés par Yup
    });

    useEffect(() => {
        methods.reset(defaultValues);
    }, [defaultValues, methods]);

    const [create, workspace] = useCreateWorkspaceMutation();
    const workspaces = useGetWorkspacesQuery(null);
    const [update, updateWorkspace] = useUpdateWorkspaceMutation();
    const [upload, media] = useUploadMutation();
    const clients = useGetClientsNameQuery(null);
    const [, setClientsName] = useState<any>([]);
    const [, setBqTable] = useState<any>(methods.getValues("bqTable") || "");
    const { enqueueSnackbar } = useSnackbar();
    const router = useRouter();



    const onSubmit = (form: Data) => {
        const { logo, ...workspace } = form;

        const request = workspace.id ? update(workspace).unwrap().then(() => {
            enqueueSnackbar(t('workspacesTerms.workspace_update_successfully'), {
                variant: 'success',
            });

            if (onCancel) {
                onCancel();
            }
        }) : create(workspace).unwrap().then(async (result) => {
            enqueueSnackbar(t('workspacesTerms.workspace_create_successfully'), {
                variant: 'success',
            });
            await workspaces.refetch();
            router.push(`/workspaces/${result.id}/setup?workspace=${result.id}`);
        });

        if (logo) {
            request.then((res: any) => {
                const formData = new FormData();
                formData.append("file", logo);
                formData.append("resource_type", "workspace");
                formData.append("resource_id", res.id.toString());
                upload(formData);
            });
        }
    }

    const [error, status] = useMemo(() => {
        let err = null;
        if (workspace.isError) {
            err = workspace.error;
        }
        if (media.isError) {
            err = media.error;
        }
        if (err && 'data' in err) {
            return [err.data as ErrorResponse, err.status];
        } else if (err && 'error' in err) {
            return [err.data as any, err.status];
        } else {
            return [err, err?.code];
        }
    }, [workspace.isError, media.isError]);

    const getErrorMessage = () => {
        return 'message' in error && typeof error.message === "string" ? error.message : 'Unknown error occurred';
    }

    useEffect(() => {
        if (clients?.data) {
            setClientsName(clients?.data);
        }
        const table = methods.getValues('bqTable');
        if (table.length > 0) {
            setBqTable(table);
        }

    }, [methods, clients]);

    const { data } = useGetCurrenciesQuery();

    const currencyOptions = data?.currencies
        .filter((v: any, i: any, self: any) => self.findIndex((c: any) => c.currency_code === v.currency_code) === i)
        .map((c: any) => ({
            label: `${c.currency_name} (${c.currency_code})`,
            value: c.currency_code.toLowerCase(),
        })) || [];


    useEffect(() => {
        const urlValue = methods.watch('url');
        if (urlValue) {
            const domain = urlValue.replace(/(https?:\/\/)?(www\.)?/, '')
                .replace(/\.[a-z]{2,}(\.[a-z]{2,})?$/, '');

            let sanitizedBQTable = domain.replace(/[-.\s]/g, '').replace(/[^\w]/g, '');

            const existingBqTables = workspaces?.data?.map((ws: any) => ws.bqTable) || [];
            let counter = 1;
            let originalBqTable = sanitizedBQTable;

            while (existingBqTables.includes(sanitizedBQTable)) {
                sanitizedBQTable = `${originalBqTable}${counter}`;
                counter++;
            }

            methods.setValue('bqTable', sanitizedBQTable.toLowerCase());
        }
    }, [methods.watch('url'), workspaces?.data]);

    const domainRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/;

    return (
        <FormProvider autoComplete="off" methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
            {status === 400 && (
                <Alert severity="error">
                    Check the form for errors
                </Alert>
            )}
            {status === 500 && (
                <Alert severity="error">
                    Unexpected error has occurred
                </Alert>
            )}
            {status && !([500, 400] as any[]).includes(status) && error && (
                <Alert severity="error">
                    {getErrorMessage()}
                </Alert>
            )}
            <Stack padding={1} spacing={3}>

                <Stack width={'100%'} flexDirection={'row'} gap={2}>
                    <RHFTextField fullWidth label={"Name"} name="name" />
                    <RHFTextField fullWidth label={"Domain"} name="url" />
                </Stack>

                <Stack width={'100%'} flexDirection={'row'} gap={2}>
                    <RHFSelect sx={{ width: '100%' }} label="Template" name="template">
                        <MenuItem value={"E-commerce"}>E-commerce</MenuItem>
                        <MenuItem value={'Lead'}>Lead</MenuItem>
                    </RHFSelect>

                    <Controller
                        name="currency"
                        control={methods.control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                options={currencyOptions}
                                value={currencyOptions.find((option: any) => option.value === field.value) || null}
                                getOptionLabel={(option) => option.value}
                                onChange={(_, newValue) => field.onChange(newValue?.value || "")}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Currency"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.value}>
                                        {option.label}
                                    </li>
                                )}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                sx={{ width: '100%' }}
                            />
                        )}
                    />

                    {/* Champ Platform */}
                    <RHFSelect sx={{ width: '100%' }} label="Platform" name="platform">
                        <MenuItem value="Google">Google</MenuItem>
                        <MenuItem value="Bing">Bing</MenuItem>
                    </RHFSelect>

                    {/* Champ Friendly Domains */}
                    <Controller
                        name="friendlyDomains"
                        control={methods.control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                multiple
                                freeSolo
                                options={[]}
                                value={field.value || []}
                                onChange={(_, newValue) => field.onChange(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Friendly Domains"
                                        placeholder="Add a domain"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                                sx={{ width: '100%' }}
                            />
                        )}
                    />

                </Stack>


                {/* Google Scope + User Email block */}
                <ComponentBlock direction={"column"} sx={{ marginTop: 3 }} spacing={2} title="Google Integration">
                    {
                        methods.watch('integrations')?.map((user, index) => {
                            return (
                                (

                                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center" width={"100%"}>
                                        <Controller
                                            name={`integrations.${index}.scopes`}
                                            control={methods.control}
                                            render={
                                                ({ field }) => (
                                                    <Autocomplete
                                                        {...field}
                                                        disableCloseOnSelect
                                                        fullWidth
                                                        multiple
                                                        options={GOOGLE_ANALYTICS_SCOPES_OPTIONS}
                                                        value={field.value?.map((scope) => GOOGLE_ANALYTICS_SCOPES_OPTIONS.find((option) => option.value === scope)) ?? []}
                                                        getOptionLabel={(option) => option?.label ?? ""}
                                                        onChange={(_, newValue) => field.onChange(newValue.map((opt) => opt?.value).filter((opt) => opt !== undefined) as GOOGLE_ANALYTICS_SCOPES[])}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Platform"
                                                                placeholder="Select Platform"
                                                                error={!!methods.formState.errors.integrations?.[index]?.scopes}
                                                            />
                                                        )}
                                                        renderTags={(value, getTagProps) =>
                                                            value.slice(0, 1).map((option, index) => (
                                                                <Chip variant="outlined" label={option?.label} {...getTagProps({ index })} />
                                                            )).concat(value.length > 1 ? <Chip variant="outlined" label={`+${value.length - 1}`} /> : [])
                                                        }
                                                        renderOption={(props, option) => (
                                                            <li {...props} key={option?.value}>
                                                                {option?.label}
                                                            </li>
                                                        )}
                                                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                                    />
                                                )
                                            }
                                        />
                                        <RHFTextField
                                            fullWidth
                                            label="Client Email"
                                            name={`integrations.${index}.email`}
                                            error={!!methods.formState.errors.integrations?.[index]?.email}
                                        />
                                        <IconButton onClick={() => {
                                            methods.setValue('integrations', methods.getValues('integrations')?.filter((_, i) => i !== index));
                                        }}>
                                            <Iconify icon="solar:trash-bin-trash-bold" sx={{ color: "red" }} />
                                        </IconButton>
                                    </Stack>
                                )
                            )
                        })
                    }
                    <Button variant={"contained"} fullWidth onClick={() => {
                        methods.setValue('integrations', [...(methods.getValues('integrations') || []), { email: '', scopes: [] }]);
                    }}>
                        Add
                    </Button>
                </ComponentBlock>

                <Stack spacing={2} alignItems={"end"} py={3} direction={{ xs: "column-reverse", md: "row-reverse" }}>
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={workspace.isLoading || media.isLoading || updateWorkspace.isLoading}
                        variant="contained"
                        type="submit"
                    >
                        {values?.id ? "Update" : "Create"}
                    </LoadingButton>
                </Stack>
            </Stack>
        </FormProvider>
    );
}